import { trackSelfDescribingEvent, CommonEventProperties, SelfDescribingJson } from '@snowplow/browser-tracker';
// Automatically generated by Snowtype

/**
 * Schema for a link click event
 */
export type LinkClick = {
    /**
     * The component where the click happened. Possible values include menu, footer, breadcrumb,
     * card, utility bar, or other.
     */
    category: Category;
    /**
     * The parent structure containing the link element. I.e: slider, grid, etc
     */
    container?: null | string;
    /**
     * List of CSS classes applied to the element that was clicked.
     */
    element_classes?: string[] | null;
    /**
     * The inner content or text of the clickable element.
     */
    element_content?: null | string;
    /**
     * The target attribute of the element, if applicable (e.g., '_blank' for opening links in a
     * new tab).
     */
    element_target?: null | string;
    /**
     * Unique identifier for the element that was clicked.
     */
    identifier: string;
    /**
     * Indicates whether the link points to an external site.
     */
    outbound: boolean;
    /**
     * The URL that the link points to.
     */
    target_url: string;
}

/**
 * The component where the click happened. Possible values include menu, footer, breadcrumb,
 * card, utility bar, or other.
 */
export type Category = "menu" | "footer" | "breadcrumb" | "card" | "utility bar" | "other";

/**
 * Schema for a blog post content entity
 */
export type BlogPost = {
    /**
     * The author of the blog post. Example: 'Lotte Sodemann Sørensen'.
     */
    author: string;
    /**
     * List of category tags associated with the blog post. Each item is a string representing a
     * specific category.
     */
    category: string[];
    /**
     * The date and time when the blog post was published, in ISO 8601 format. Example:
     * '2024-03-05T14:30:00Z'.
     */
    date_published?: Date | null;
    /**
     * The title of the blog post, such as: 'This is why HR teams should care about robotics'.
     */
    name: string;
}

/**
 * Schema for a case story content entity
 */
export type CaseStory = {
    /**
     * List of application types or tags associated with the case story. Each item is a string
     * representing a specific application.
     */
    application_type?: string[] | null;
    /**
     * List of collaborative robots (cobots) used by the company in the case story. Each item is
     * a string representing a specific cobot.
     */
    cobot_used?: string[] | null;
    /**
     * The name of the company featured in the case story. Example: 'SESI/SENAI'.
     */
    company?: null | string;
    /**
     * The size of the company, typically represented as a number or range. Example: '1000+'.
     */
    company_size?: null | string;
    /**
     * List of components utilized by the company in the case story. Each item is a string
     * representing a specific component.
     */
    components_used?: string[] | null;
    /**
     * The country where the case story takes place. Example: 'Brazil'.
     */
    country?: null | string;
    /**
     * The date and time when the case story was published, in ISO 8601 format. Example:
     * '2024-03-05T14:30:00Z'.
     */
    date_published?: Date | null;
    /**
     * The industry sector of the company. Example: 'Education'.
     */
    industry?: null | string;
    /**
     * The title of the case story, such as: 'UR cobots help prepare students for the industry
     * of the future'.
     */
    name: string;
    /**
     * The geographic region where the case story takes place. Example: 'South America'.
     */
    region?: null | string;
}

/**
 * Schema for a news article content entity
 */
export type NewsArticle = {
    /**
     * List of categories or tags associated with the news article. Each item is a string
     * representing a specific category.
     */
    category: string[] | null;
    /**
     * The date and time when the news article was published, in ISO 8601 format. Example:
     * '2024-05-03T14:30:00Z'.
     */
    date_published?: Date | null;
    /**
     * URL of the featured image for the news article.
     */
    featured_image?: null | string;
    /**
     * The title of the news article. Example: 'Universal Robots Emerges as Preferred Robotics
     * Platform for AI Solutions at Automate 2024'.
     */
    title: string;
}

/**
 * Schema for a UR+ Showroom Product
 */
export type UrPlusShowroomProduct = {
    /**
     * Application that a product is used for. (E.g.: Machine Tending, Palletizing, etc)
     */
    application_type?: null | string;
    /**
     * Brand that manufacturers the product.
     */
    brand?: null | string;
    /**
     * The category of product. Use a consistent separator to express multiple levels. E.g.
     * Application Kit/Cobot/Component
     */
    category?: null | string;
    /**
     * The Universal Robots Products that this product is compatible to. Use a consistent
     * separator to express multiple levels. E.g. UR3/UR5/UR10
     */
    compatibility?: null | string;
    /**
     * Identifier/Name/Url for the creative presented on a list or product view.
     */
    creative_id?: null | string;
    /**
     * The SKU or product ID.
     */
    id: string;
    /**
     * The name or title of the product.
     */
    name?: null | string;
    /**
     * The type of product. Use a consistent separator to express multiple levels. E.g. 2D
     * Camera/Cable Guidance/Gripper - Magnetic
     */
    product_type?: null | string;
}

/**
 * Schema for a button click event, specifically for button elements and not divs styled as
 * buttons.
 */
export type ButtonClick = {
    /**
     * An array of class names from the button clicked.
     */
    element_classes?: string[] | null;
    /**
     * The text on the button clicked.
     */
    element_content?: null | string;
    /**
     * The identifier of the button element.
     */
    identifier: string;
    /**
     * The URL that the link points to.
     */
    target_url?: null | string;
}

/**
 * Schema for an Ecommerce action
 */
export type EcommerceAction = {
    /**
     * Standard ecommerce actions.
     */
    type: Type;
}

/**
 * Standard ecommerce actions.
 */
export type Type = "add_to_cart" | "remove_from_cart" | "product_view" | "list_click" | "list_view" | "checkout_step" | "transaction" | "trns_error";

/**
 * Schema for a cart entity in Ecommerce
 */
export type Cart = {
    /**
     * The unique ID representing this cart.
     */
    cart_id: string;
    /**
     * The currency used for this cart (ISO 4217).
     */
    currency: string;
    /**
     * Total number of items in the cart.
     */
    total_items?: number | null;
    /**
     * The total value of the cart after this interaction.
     */
    total_value: number;
}

/**
 * Schema for an order entity in the Marketplace
 */
export type Order = {
    /**
     * If user added insurance to shipment.
     */
    added_insurance: boolean;
    /**
     * The currency used for the transaction (ISO 4217).
     */
    currency: string;
    /**
     * Opportunity number.
     */
    opportunity_number?: null | string;
    /**
     * The GUID of the order.
     */
    order_id: string;
    /**
     * The purchase order number.
     */
    purchase_order_number: string;
    /**
     * The recommended selling price for the products ordered.
     */
    recommended_selling_price?: number | null;
    /**
     * The order total revenue.
     */
    revenue: number;
    /**
     * Full shipping address.
     */
    shipping_address?: null | string;
    /**
     * Shipping address city.
     */
    shipping_city?: null | string;
    /**
     * Shipping address country. Use the lowercase, 2-letter country codes in ISO 3166-1 alpha-2.
     */
    shipping_country?: null | string;
    /**
     * Shipping address first name.
     */
    shipping_first_name?: null | string;
    /**
     * Shipping address last name.
     */
    shipping_last_name?: null | string;
    /**
     * Shipping address postcode.
     */
    shipping_postcode?: null | string;
    /**
     * Total quantity of items in the placed order.
     */
    total_items: number;
    /**
     * If user uploaded the purchase order file.
     */
    uploaded_purchase_order_file: boolean;
    [property: string]: any;
}

/**
 * Schema for a Marketplace Product
 */
export type Product = {
    /**
     * A list of non-hierarchical (tag-like) application label that a product is used for.
     * (E.g.: Machine Tending, Palletizing, etc)
     */
    application_type?: string[] | null;
    /**
     * Brand that manufacturers the product.
     */
    brand: string;
    /**
     * The category the product belongs to
     */
    category: string;
    /**
     * A list of non-hierarchical (tag-like) Universal Robots Products that this product is
     * compatible to
     */
    compatibility: string[];
    /**
     * URL for the media/image presented on a list or product view.
     */
    creative_id?: null | string;
    /**
     * The currency in which the product is being priced (ISO 4217).
     */
    currency?: null | string;
    /**
     * The SKU or product ID.
     */
    id: string;
    /**
     * The name or title of the product.
     */
    name: string;
    /**
     * The position the product was presented in a list of products E.g. search results, product
     * list page.
     */
    position?: number | null;
    /**
     * The price of the product at the current time.
     */
    price?: number | null;
    /**
     * A list of non-hierarchical (tag-like) product type labels the product belongs to
     */
    product_type?: string[] | null;
    /**
     * The total quantity of the product taking part in the ecommerce action.
     */
    quantity?: number | null;
    [property: string]: any;
}

/**
 * A user submits a form. This event does not indicate that the form was fully processed and
 * the data was saved, but rather simply that the user attempted to submit the form. If the
 * form fails validation or encounters an unrelated error, fire form_error subsequent to
 * this. If the form passes validation and is successfully submitted, fire the form_complete
 * event.
 */
export type FormSubmit = {
}

/**
 * Schema for a form component
 */
export type Form = {
    /**
     * The category of the form, which can be picked up from the value within the 1st and 2nd
     * dash from the form name, or any of the following values
     * mql/event/training/content/service/other
     */
    category: string;
    /**
     * The name of the form in Marketing Cloud Account Engagement or any proxy name
     */
    name: string;
}

/**
 * Schema for list_filter event
 */
export type ListFilterEventSchema = {
    /**
     * The category of the filter selected
     */
    filter_category: string;
    /**
     * The filter values selected
     */
    filter_values: string[];
    /**
     * The number of items returned from the filter applied
     */
    list_count?: number | null;
    /**
     * The type of list where the filter is applied. I.e.: case_story, marketplace, news_center
     */
    list_type?: null | string;
}

/**
 * A user has a form element visible in the screen
 */
export type FormView = {
}

/**
 * Schema for a checkout step entity in the Marketplace
 */
export type CheckoutStep = {
    /**
     * Checkout step index.
     */
    step: number;
    /**
     * Checkout step name.
     */
    step_action: StepAction;
}

/**
 * Checkout step name.
 */
export type StepAction = "begun_checkout" | "added_shipping_address" | "added_shipping_method" | "added_payment_method";

/**
 * A share event. Send a content entity together with this event to add context around which
 * content was shared. I.e.: share + blog_post
 */
export type Share = {
    /**
     * The method in which the content is shared.
     */
    method: string;
}

/**
 * Schema for tracking user interactions with wizard steps
 */
export type WizardStepInteraction = {
    /**
     * The action the user took on the step
     */
    action: Action;
    /**
     * An array of questions presented in the step and the user's responses
     */
    questions?: Question[] | null;
    /**
     * The name or title of the wizard step
     */
    step_name?: null | string;
    /**
     * The sequential number representing the wizard step the user interacted with
     */
    step_number: number;
    /**
     * The name of the wizard instance (useful if there are multiple wizards on the site). E.g.,
     * 'customer_matchmaker'
     */
    wizard_name: string;
    [property: string]: any;
}

/**
 * The action the user took on the step
 */
export type Action = "started" | "completed";

/**
 * An object representing a question and the user's response
 */
export type Question = {
    /**
     * A unique identifier for the question. It could be a field name that is immutable
     */
    question_id: string;
    /**
     * The text of the question. In other words, this would be the field label.
     */
    question_text?: null | string;
    /**
     * The user's response to the question
     */
    response: string[] | boolean | null | string;
}

/**
 * Schema to capture events from chatbot embed in our website
 */
export type ChatbotInteraction = {
    /**
     * Type of chatbot event (e.g. 'trigger' when the bot is opened, 'user_trigger' when user
     * manually opens it, 'start_discussion' when conversation starts, 'button_click' for an
     * option button press, 'form_complete' for a completed form)
     */
    event_type: ChatbotInteractionEventType;
    /**
     * Label(s) or category(ies) of the clicked option. Each array item is one label string
     * (e.g. for multi-select answers).
     */
    label?: string[] | null;
    /**
     * Text content associated with the event (e.g. the message shown or text input in the bot)
     */
    message_text?: null | string;
    /**
     * Details captured when a lead is converted (present only for submission events). Each item
     * represents a field of user input (such as name, email, etc).
     */
    submission_data?: SubmissionDatum[] | null;
}

/**
 * Type of chatbot event (e.g. 'trigger' when the bot is opened, 'user_trigger' when user
 * manually opens it, 'start_discussion' when conversation starts, 'button_click' for an
 * option button press, 'form_complete' for a completed form)
 */
export type ChatbotInteractionEventType = "trigger" | "user_trigger" | "start_discussion" | "button_click" | "form_complete";

export type SubmissionDatum = {
    /**
     * Name of the field or question (e.g. 'email', 'name')
     */
    field?: null | string;
    /**
     * User-provided answer or input for that field
     */
    value?: null | string;
}

/**
 * Context entity providing metadata about the chatbot, including name, nickname, category,
 * and interface type
 */
export type Chatbot = {
    /**
     * Custom category of the bot
     */
    bot_category?: null | string;
    /**
     * Name of the chatbot (as configured in Leadoo)
     */
    bot_name: null | string;
    /**
     * Internal alias given to the bot in Leadoo settings
     */
    bot_nickname?: null | string;
    /**
     * Type of bot interface (embed, chat, CTA, modal) indicating how the bot is presented on
     * the site
     */
    bot_type: null | string;
}

/**
 * A user encounters an error while attempting to complete a form. This event is typically
 * fired subsequent to form_submit if validation fails or another error occurs (server
 * error, etc.).
 */
export type FormError = {
    /**
     * The type of error that occurred.
     */
    type: string;
}

/**
 * This schema captures the details of a tab selection event.
 */
export type TabSelect = {
    /**
     * CSS classes applied to the tab element that was selected.
     */
    element_classes?: string[] | null;
    /**
     * The inner content or text of the selected tab.
     */
    element_content?: null | string;
    /**
     * The unique identifier of where the click happened on the page
     */
    identifier: null | string;
    /**
     * The numerical index of the active tab position (0-indexed).
     */
    index: number;
}

/**
 * Schema for consent preferences selection event
 */
export type ConsentPreferences = {
    /**
     * GDPR lawful basis for data collection & processing.
     */
    basisForProcessing: BasisForProcessing;
    /**
     * The scopes allowed after the user finalized his selection of consent preferences. E.g
     * ['analytics', 'functional', 'advertisement'].
     */
    consentScopes: string[];
    /**
     * URI of the privacy policy related document.
     */
    consentUrl: string;
    /**
     * Version of the privacy policy related document.
     */
    consentVersion: string;
    /**
     * The domains for which this consent allows these preferences to persist to.
     */
    domainsApplied: string[];
    /**
     * The action for the consent preferences of a user.
     */
    eventType: EventType;
    /**
     * Determine if GDPR applies based on the user's geo-location.
     */
    gdprApplies?: boolean | null;
}

/**
 * GDPR lawful basis for data collection & processing.
 */
export type BasisForProcessing = "consent" | "contract" | "legal_obligation" | "vital_interests" | "public_task" | "legitimate_interests";

/**
 * The action for the consent preferences of a user.
 */
export type EventType = "deny_all" | "allow_all" | "allow_selected" | "pending" | "implicit_consent" | "withdrawn" | "expired";

/**
 * Schema for user login and logout events
 */
export type UserDetect = {
    /**
     * The type of event, either login or logout.
     */
    event_type: UserDetectEventType;
}

/**
 * The type of event, either login or logout.
 */
export type UserDetectEventType = "login" | "logout";

/**
 * This schema defines the structure for capturing data from successful form submissions.
 */
export type FormComplete = {
    /**
     * Specifies the type of application associated with the form submission, based on the
     * Pardot field 'Application__c'.
     */
    application_type?: null | string;
    /**
     * Non-Normalized User's country from the form field 'Country'.
     */
    country?: null | string;
    /**
     * Non-Normalized User's email address from the form field 'Email'.
     */
    email: string;
    /**
     * Non-Normalized User's first name from the form field 'FirstName', with a maximum
     * character limit of 40.
     */
    first_name?: null | string;
    /**
     * User's postal or zip code from Pardot form field.
     */
    industry?: null | string;
    /**
     * The purpose or intent behind the form submission, captured from the Pardot field
     * 'Purpose_of_Request__c'. This field provides insight into the user's expectations and
     * requirements from the form interaction.
     */
    intent?: null | string;
    /**
     * Non-Normalized User's last name from the form field 'LastName', with a maximum character
     * limit of 80.
     */
    last_name?: null | string;
    /**
     * Non-Normalized User's phone number from the form field 'Phone'.
     */
    phone?: null | string;
    /**
     * Pardot form field 'User_Role' value
     */
    role?: null | string;
    /**
     * Pardot form field 'UserRoleSubcategory' value
     */
    role_subcategory?: null | string;
    /**
     * SHA-256 hash of the user's country. Before hashing normalize value to lowercase, 2-letter
     * country codes in ISO 3166-1 alpha-2.
     */
    sha256_country?: null | string;
    /**
     * SHA-256 hash of the user's email address. Before hashing normalize value to remove
     * leading and trailing whitespaces, convert the text to lowercase, remove all periods (.)
     * that precede the domain name in gmail.com and googlemail.com email addresses.
     */
    sha256_email: string;
    /**
     * SHA-256 hash of the user's first name. Before hashing normalize the value to remove
     * leading and trailing whitespaces and convert the text to lowercase.
     */
    sha256_first_name?: null | string;
    /**
     * SHA-256 hash of the user's last name. Before hashing normalize the value to remove
     * leading and trailing whitespaces and convert the text to lowercase.
     */
    sha256_last_name?: null | string;
    /**
     * SHA-256 hash of the user's phone number. Before hashing normalize value to remove
     * symbols, letters, and any leading zeros. Phone numbers must include a country code to be
     * used for advertising matching (e.g., the number 1 must precede a phone number in the
     * United States).
     */
    sha256_phone?: null | string;
    /**
     * SHA-256 hash of the user's phone number in E.164 format.
     */
    sha256_phone_e164?: null | string;
    /**
     * SHA-256 hash of the user's state or region. Before hashing normalize value to use the
     * 2-character ANSI abbreviation code in lowercase for US States. Normalize states outside
     * the U.S. in lowercase with no punctuation, no special characters, and no spaces.
     */
    sha256_state?: null | string;
    /**
     * SHA-256 hash of the user's postal or zip code. Before hashing normalize value to
     * lowercase with no spaces and no dash. Use only the first 5 digits for U.S. zip codes. Use
     * the area, district, and sector format for the UK.
     */
    sha256_zipcode?: null | string;
    /**
     * Non-Normalized User's state or region from the corresponding state field depending on the
     * country (e.g., 'State_Region_AU__c' for Australia).
     */
    state?: null | string;
    /**
     * Planned timeline for the project or engagement as specified in the Pardot form field
     * 'Project_Plan_Timeline__c'.
     */
    timeline?: null | string;
    /**
     * Non-Normalized User's postal or zip code from the form.
     */
    zipcode?: null | string;
}

/**
 * Schema for custom page properties
 */
export type Page = {
    /**
     * The language that the page is based in.
     */
    language: string;
    /**
     * The locale version of the site that is running.
     */
    locale?: null | string;
    /**
     * The type of the page that was visited E.g. homepage, product page, blog.
     */
    type: string;
}

/**
 * Schema for a user entity in universal-robots.com
 */
export type User = {
    /**
     * The company or affiliation captured one OneLogin
     */
    company_name?: null | string;
    /**
     * The country captured one OneLogin. 2-letter country codes in ISO 3166-1 alpha-2
     */
    country?: null | string;
    /**
     * The user's email address.
     */
    email: string;
    /**
     * Whether or not the user has marketplace access.
     */
    has_marketplace_access?: boolean | null;
    /**
     * The user's role.
     */
    role?: null | string;
    /**
     * The user salesforce id.
     */
    salesforce_id?: null | string;
    /**
     * The userType captured on OneLogin.
     */
    type?: null | string;
    /**
     * A drill-down of the userType captured on OneLogin
     */
    type_detailed?: null | string;
    [property: string]: any;
}

/**
 * Schema for a transaction error or rejection entity in ecommerce.
 */
export type TransactionError = {
    /**
     * Error-identifying code for the transaction issue. E.g. E522
     */
    error_code?: null | string;
    /**
     * Longer description for the error occurred in the transaction.
     */
    error_description?: null | string;
    /**
     * Shortcode for the error occurred in the transaction. E.g. declined_by_payment_method, etc
     */
    error_shortcode?: null | string;
    /**
     * The resolution selected for the error scenario. E.g. retry_allowed, user_blacklisted,
     * contact_user, default
     */
    resolution?: null | string;
}

/**
 * This schema captures the details of a file download event, including the file's metadata
 * and the element attributes that triggered the download.
 */
export type FileDownload = {
    /**
     * List of CSS classes applied to the element that was clicked.
     */
    element_classes?: string[] | null;
    /**
     * The inner content or text of the clickable element.
     */
    element_content?: null | string;
    /**
     * The target attribute of the element, if applicable (e.g., '_blank' for opening links in a
     * new tab).
     */
    element_target?: null | string;
    /**
     * The extensio of the file downloaded.
     */
    file_extension: null | string;
    /**
     * The file name.
     */
    file_name: string;
    /**
     * The URL that the link points to.
     */
    target_url: string;
}

/**
 * Schema for an event when a user starts filling out a form.
 */
export type FormStart = {
}

/**
 * Creates a Snowplow Event Specification entity.
 */
export function createEventSpecification(eventSpecification: EventSpecification){
    return {
        schema:
            'iglu:com.snowplowanalytics.snowplow/event_specification/jsonschema/1-0-2',
        data: eventSpecification,
    }
}

/**
 * Automatically attached context for event specifications
 */
interface EventSpecification {
    id: string;
    name: string;
    data_product_id: string;
    data_product_name: string;
}

type ContextsOrTimestamp<T = any> = Omit<CommonEventProperties<T>, 'context'> & { context?: SelfDescribingJson<T>[] | null | undefined }
/**
 * Track a Snowplow event for LinkClick.
 * Schema for a link click event
 */
export function trackLinkClick<T extends {} = any>(linkClick: LinkClick & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = linkClick;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/link_click/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LinkClick entity.
 */
export function createLinkClick(linkClick: LinkClick){
    return {
        schema: 'iglu:com.universalrobots/link_click/jsonschema/1-0-3',
        data: linkClick
    }
}
/**
 * Track a Snowplow event for BlogPost.
 * Schema for a blog post content entity
 */
export function trackBlogPost<T extends {} = any>(blogPost: BlogPost & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = blogPost;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/blog_post/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow BlogPost entity.
 */
export function createBlogPost(blogPost: BlogPost){
    return {
        schema: 'iglu:com.universalrobots/blog_post/jsonschema/1-0-0',
        data: blogPost
    }
}
/**
 * Track a Snowplow event for CaseStory.
 * Schema for a case story content entity
 */
export function trackCaseStory<T extends {} = any>(caseStory: CaseStory & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = caseStory;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/case_story/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CaseStory entity.
 */
export function createCaseStory(caseStory: CaseStory){
    return {
        schema: 'iglu:com.universalrobots/case_story/jsonschema/1-0-0',
        data: caseStory
    }
}
/**
 * Track a Snowplow event for NewsArticle.
 * Schema for a news article content entity
 */
export function trackNewsArticle<T extends {} = any>(newsArticle: NewsArticle & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = newsArticle;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/news_article/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NewsArticle entity.
 */
export function createNewsArticle(newsArticle: NewsArticle){
    return {
        schema: 'iglu:com.universalrobots/news_article/jsonschema/1-0-0',
        data: newsArticle
    }
}
/**
 * Track a Snowplow event for UrPlusShowroomProduct.
 * Schema for a UR+ Showroom Product
 */
export function trackUrPlusShowroomProduct<T extends {} = any>(urPlusShowroomProduct: UrPlusShowroomProduct & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = urPlusShowroomProduct;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/ur_plus_showroom_product/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UrPlusShowroomProduct entity.
 */
export function createUrPlusShowroomProduct(urPlusShowroomProduct: UrPlusShowroomProduct){
    return {
        schema: 'iglu:com.universalrobots/ur_plus_showroom_product/jsonschema/1-0-1',
        data: urPlusShowroomProduct
    }
}
/**
 * Track a Snowplow event for ButtonClick.
 * Schema for a button click event, specifically for button elements and not divs styled as buttons.
 */
export function trackButtonClick<T extends {} = any>(buttonClick: ButtonClick & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = buttonClick;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/button_click/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ButtonClick entity.
 */
export function createButtonClick(buttonClick: ButtonClick){
    return {
        schema: 'iglu:com.universalrobots/button_click/jsonschema/1-1-0',
        data: buttonClick
    }
}
/**
 * Track a Snowplow event for EcommerceAction.
 * Schema for an Ecommerce action
 */
export function trackEcommerceAction<T extends {} = any>(ecommerceAction: EcommerceAction & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ecommerceAction;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/ecommerce_action/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EcommerceAction entity.
 */
export function createEcommerceAction(ecommerceAction: EcommerceAction){
    return {
        schema: 'iglu:com.universalrobots/ecommerce_action/jsonschema/1-0-0',
        data: ecommerceAction
    }
}
/**
 * Track a Snowplow event for Cart.
 * Schema for a cart entity in Ecommerce
 */
export function trackCart<T extends {} = any>(cart: Cart & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cart;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/cart/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Cart entity.
 */
export function createCart(cart: Cart){
    return {
        schema: 'iglu:com.universalrobots/cart/jsonschema/2-0-0',
        data: cart
    }
}
/**
 * Track a Snowplow event for Order.
 * Schema for an order entity in the Marketplace
 */
export function trackOrder<T extends {} = any>(order: Order & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = order;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/order/jsonschema/3-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Order entity.
 */
export function createOrder(order: Order){
    return {
        schema: 'iglu:com.universalrobots/order/jsonschema/3-0-0',
        data: order
    }
}
/**
 * Track a Snowplow event for Product.
 * Schema for a Marketplace Product
 */
export function trackProduct<T extends {} = any>(product: Product & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = product;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/product/jsonschema/5-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Product entity.
 */
export function createProduct(product: Product){
    return {
        schema: 'iglu:com.universalrobots/product/jsonschema/5-0-0',
        data: product
    }
}
/**
 * Track a Snowplow event for FormSubmit.
 * A user submits a form. This event does not indicate that the form was fully processed and the data was saved, but rather simply that the user attempted to submit the form. If the form fails validation or encounters an unrelated error, fire form_error subsequent to this. If the form passes validation and is successfully submitted, fire the form_complete event.
 */
export function trackFormSubmit<T extends {} = any>(formSubmit: FormSubmit & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formSubmit;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/form_submit/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormSubmit entity.
 */
export function createFormSubmit(formSubmit: FormSubmit){
    return {
        schema: 'iglu:com.universalrobots/form_submit/jsonschema/1-0-0',
        data: formSubmit
    }
}
/**
 * Track a Snowplow event for Form.
 * Schema for a form component
 */
export function trackForm<T extends {} = any>(form: Form & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = form;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/form/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Form entity.
 */
export function createForm(form: Form){
    return {
        schema: 'iglu:com.universalrobots/form/jsonschema/1-0-0',
        data: form
    }
}
/**
 * Track a Snowplow event for ListFilterEventSchema.
 * Schema for list_filter event
 */
export function trackListFilterEventSchema<T extends {} = any>(listFilterEventSchema: ListFilterEventSchema & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = listFilterEventSchema;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/list_filter/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ListFilterEventSchema entity.
 */
export function createListFilterEventSchema(listFilterEventSchema: ListFilterEventSchema){
    return {
        schema: 'iglu:com.universalrobots/list_filter/jsonschema/1-0-1',
        data: listFilterEventSchema
    }
}
/**
 * Track a Snowplow event for FormView.
 * A user has a form element visible in the screen
 */
export function trackFormView<T extends {} = any>(formView: FormView & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formView;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/form_view/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormView entity.
 */
export function createFormView(formView: FormView){
    return {
        schema: 'iglu:com.universalrobots/form_view/jsonschema/1-0-0',
        data: formView
    }
}
/**
 * Track a Snowplow event for CheckoutStep.
 * Schema for a checkout step entity in the Marketplace
 */
export function trackCheckoutStep<T extends {} = any>(checkoutStep: CheckoutStep & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = checkoutStep;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/checkout_step/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CheckoutStep entity.
 */
export function createCheckoutStep(checkoutStep: CheckoutStep){
    return {
        schema: 'iglu:com.universalrobots/checkout_step/jsonschema/1-0-1',
        data: checkoutStep
    }
}
/**
 * Track a Snowplow event for Share.
 * A share event. Send a content entity together with this event to add context around which content was shared. I.e.: share + blog_post
 */
export function trackShare<T extends {} = any>(share: Share & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = share;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/share/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Share entity.
 */
export function createShare(share: Share){
    return {
        schema: 'iglu:com.universalrobots/share/jsonschema/1-0-1',
        data: share
    }
}
/**
 * Track a Snowplow event for WizardStepInteraction.
 * Schema for tracking user interactions with wizard steps
 */
export function trackWizardStepInteraction<T extends {} = any>(wizardStepInteraction: WizardStepInteraction & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = wizardStepInteraction;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/wizard_step_interaction/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WizardStepInteraction entity.
 */
export function createWizardStepInteraction(wizardStepInteraction: WizardStepInteraction){
    return {
        schema: 'iglu:com.universalrobots/wizard_step_interaction/jsonschema/1-0-3',
        data: wizardStepInteraction
    }
}
/**
 * Track a Snowplow event for ChatbotInteraction.
 * Schema to capture events from chatbot embed in our website
 */
export function trackChatbotInteraction<T extends {} = any>(chatbotInteraction: ChatbotInteraction & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatbotInteraction;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/chatbot_interaction/jsonschema/1-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatbotInteraction entity.
 */
export function createChatbotInteraction(chatbotInteraction: ChatbotInteraction){
    return {
        schema: 'iglu:com.universalrobots/chatbot_interaction/jsonschema/1-1-1',
        data: chatbotInteraction
    }
}
/**
 * Track a Snowplow event for Chatbot.
 * Context entity providing metadata about the chatbot, including name, nickname, category, and interface type
 */
export function trackChatbot<T extends {} = any>(chatbot: Chatbot & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatbot;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/chatbot/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Chatbot entity.
 */
export function createChatbot(chatbot: Chatbot){
    return {
        schema: 'iglu:com.universalrobots/chatbot/jsonschema/1-0-0',
        data: chatbot
    }
}
/**
 * Track a Snowplow event for FormError.
 * A user encounters an error while attempting to complete a form. This event is typically fired subsequent to form_submit if validation fails or another error occurs (server error, etc.).
 */
export function trackFormError<T extends {} = any>(formError: FormError & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formError;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/form_error/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormError entity.
 */
export function createFormError(formError: FormError){
    return {
        schema: 'iglu:com.universalrobots/form_error/jsonschema/1-0-0',
        data: formError
    }
}
/**
 * Track a Snowplow event for TabSelect.
 * This schema captures the details of a tab selection event.
 */
export function trackTabSelect<T extends {} = any>(tabSelect: TabSelect & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = tabSelect;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/tab_select/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TabSelect entity.
 */
export function createTabSelect(tabSelect: TabSelect){
    return {
        schema: 'iglu:com.universalrobots/tab_select/jsonschema/1-0-0',
        data: tabSelect
    }
}
/**
 * Track a Snowplow event for ConsentPreferences.
 * Schema for consent preferences selection event
 */
export function trackConsentPreferences<T extends {} = any>(consentPreferences: ConsentPreferences & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = consentPreferences;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.snowplowanalytics.snowplow/consent_preferences/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ConsentPreferences entity.
 */
export function createConsentPreferences(consentPreferences: ConsentPreferences){
    return {
        schema: 'iglu:com.snowplowanalytics.snowplow/consent_preferences/jsonschema/1-0-0',
        data: consentPreferences
    }
}
/**
 * Track a Snowplow event for UserDetect.
 * Schema for user login and logout events
 */
export function trackUserDetect<T extends {} = any>(userDetect: UserDetect & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userDetect;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/user_detect/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserDetect entity.
 */
export function createUserDetect(userDetect: UserDetect){
    return {
        schema: 'iglu:com.universalrobots/user_detect/jsonschema/1-0-1',
        data: userDetect
    }
}
/**
 * Track a Snowplow event for FormComplete.
 * This schema defines the structure for capturing data from successful form submissions.
 */
export function trackFormComplete<T extends {} = any>(formComplete: FormComplete & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formComplete;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/form_complete/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormComplete entity.
 */
export function createFormComplete(formComplete: FormComplete){
    return {
        schema: 'iglu:com.universalrobots/form_complete/jsonschema/1-1-0',
        data: formComplete
    }
}
/**
 * Track a Snowplow event for Page.
 * Schema for custom page properties
 */
export function trackPage<T extends {} = any>(page: Page & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = page;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/page/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Page entity.
 */
export function createPage(page: Page){
    return {
        schema: 'iglu:com.universalrobots/page/jsonschema/1-0-0',
        data: page
    }
}
/**
 * Track a Snowplow event for User.
 * Schema for a user entity in universal-robots.com
 */
export function trackUser<T extends {} = any>(user: User & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = user;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/user/jsonschema/4-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow User entity.
 */
export function createUser(user: User){
    return {
        schema: 'iglu:com.universalrobots/user/jsonschema/4-0-1',
        data: user
    }
}
/**
 * Track a Snowplow event for TransactionError.
 * Schema for a transaction error or rejection entity in ecommerce.
 */
export function trackTransactionError<T extends {} = any>(transactionError: TransactionError & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = transactionError;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/transaction_error/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TransactionError entity.
 */
export function createTransactionError(transactionError: TransactionError){
    return {
        schema: 'iglu:com.universalrobots/transaction_error/jsonschema/1-0-0',
        data: transactionError
    }
}
/**
 * Track a Snowplow event for FileDownload.
 * This schema captures the details of a file download event, including the file&#x27;s metadata and the element attributes that triggered the download.
 */
export function trackFileDownload<T extends {} = any>(fileDownload: FileDownload & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = fileDownload;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/file_download/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FileDownload entity.
 */
export function createFileDownload(fileDownload: FileDownload){
    return {
        schema: 'iglu:com.universalrobots/file_download/jsonschema/1-0-3',
        data: fileDownload
    }
}
/**
 * Track a Snowplow event for FormStart.
 * Schema for an event when a user starts filling out a form.
 */
export function trackFormStart<T extends {} = any>(formStart: FormStart & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formStart;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.universalrobots/form_start/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormStart entity.
 */
export function createFormStart(formStart: FormStart){
    return {
        schema: 'iglu:com.universalrobots/form_start/jsonschema/1-0-1',
        data: formStart
    }
}

/**
 * Tracks a LinkClick event specification.
 * ID: 05164f4d-a6bf-4341-a5e6-ceb318a3b57c
 */
export function trackLinkClickSpec(linkClick: LinkClick & ContextsOrTimestamp<BlogPost | CaseStory | NewsArticle | UrPlusShowroomProduct>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '05164f4d-a6bf-4341-a5e6-ceb318a3b57c',
        name: 'Link Click',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(linkClick.context)
        ? [...linkClick.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLinkClick = {
        ...linkClick,
        context,
    };

    trackLinkClick<BlogPost | CaseStory | NewsArticle | UrPlusShowroomProduct | EventSpecification>(modifiedLinkClick, trackers);
}
/**
 * Tracks a CustomerMatchmakerButtonClick event specification.
 * ID: 09da03f1-e111-4c59-96de-812da48d2c7e
 */
export function trackCustomerMatchmakerButtonClickSpec(customerMatchmakerButtonClick: ButtonClick & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '09da03f1-e111-4c59-96de-812da48d2c7e',
        name: 'Customer Matchmaker Button Click',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(customerMatchmakerButtonClick.context)
        ? [...customerMatchmakerButtonClick.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCustomerMatchmakerButtonClick = {
        ...customerMatchmakerButtonClick,
        context,
    };

    trackButtonClick<Record<string, unknown> | EventSpecification>(modifiedCustomerMatchmakerButtonClick, trackers);
}
/**
 * Tracks a Transaction event specification.
 * ID: 1512565b-ed99-4cbc-9077-cd0b73dbee55
 */
export function trackTransactionSpec(transaction: EcommerceAction & ContextsOrTimestamp<Cart | Order | Product>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '1512565b-ed99-4cbc-9077-cd0b73dbee55',
        name: 'Transaction',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(transaction.context)
        ? [...transaction.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTransaction = {
        ...transaction,
        context,
    };

    trackEcommerceAction<Cart | Order | Product | EventSpecification>(modifiedTransaction, trackers);
}
/**
 * Tracks a FormSubmit event specification.
 * ID: 1c20da97-45b4-4634-aec0-ca858c539164
 */
export function trackFormSubmitSpec(formSubmit: FormSubmit & ContextsOrTimestamp<Form | UrPlusShowroomProduct>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '1c20da97-45b4-4634-aec0-ca858c539164',
        name: 'Form Submit',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(formSubmit.context)
        ? [...formSubmit.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormSubmit = {
        ...formSubmit,
        context,
    };

    trackFormSubmit<Form | UrPlusShowroomProduct | EventSpecification>(modifiedFormSubmit, trackers);
}
/**
 * Tracks a AddToCart event specification.
 * ID: 224c956f-7597-4af4-8836-1254f26d32a2
 */
export function trackAddToCartSpec(addToCart: EcommerceAction & ContextsOrTimestamp<Cart | Product>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '224c956f-7597-4af4-8836-1254f26d32a2',
        name: 'Add to cart',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(addToCart.context)
        ? [...addToCart.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAddToCart = {
        ...addToCart,
        context,
    };

    trackEcommerceAction<Cart | Product | EventSpecification>(modifiedAddToCart, trackers);
}
/**
 * Tracks a RemoveFromCart event specification.
 * ID: 28c00c14-2034-4d28-91c3-0e5386a0d253
 */
export function trackRemoveFromCartSpec(removeFromCart: EcommerceAction & ContextsOrTimestamp<Cart | Product>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '28c00c14-2034-4d28-91c3-0e5386a0d253',
        name: 'Remove from cart',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(removeFromCart.context)
        ? [...removeFromCart.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedRemoveFromCart = {
        ...removeFromCart,
        context,
    };

    trackEcommerceAction<Cart | Product | EventSpecification>(modifiedRemoveFromCart, trackers);
}
/**
 * Tracks a ListFilter event specification.
 * ID: 2b9a91a8-0526-42ae-86cc-dddf5b57e48a
 */
export function trackListFilterSpec(listFilter: ListFilterEventSchema & ContextsOrTimestamp<BlogPost | CaseStory | NewsArticle | UrPlusShowroomProduct>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '2b9a91a8-0526-42ae-86cc-dddf5b57e48a',
        name: 'List Filter',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(listFilter.context)
        ? [...listFilter.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedListFilter = {
        ...listFilter,
        context,
    };

    trackListFilterEventSchema<BlogPost | CaseStory | NewsArticle | UrPlusShowroomProduct | EventSpecification>(modifiedListFilter, trackers);
}
/**
 * Tracks a FormView event specification.
 * ID: 4047b0e5-b0bc-4f47-9fe1-d3b0a2d4bd55
 */
export function trackFormViewSpec(formView: FormView & ContextsOrTimestamp<Form | UrPlusShowroomProduct>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '4047b0e5-b0bc-4f47-9fe1-d3b0a2d4bd55',
        name: 'Form View',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(formView.context)
        ? [...formView.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormView = {
        ...formView,
        context,
    };

    trackFormView<Form | UrPlusShowroomProduct | EventSpecification>(modifiedFormView, trackers);
}
/**
 * Tracks a CheckoutStep event specification.
 * ID: 52c5f558-a936-4b2e-95b2-79fec7789a3a
 */
export function trackCheckoutStepSpec(checkoutStep: EcommerceAction & ContextsOrTimestamp<Cart | CheckoutStep>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '52c5f558-a936-4b2e-95b2-79fec7789a3a',
        name: 'Checkout step',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(checkoutStep.context)
        ? [...checkoutStep.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCheckoutStep = {
        ...checkoutStep,
        context,
    };

    trackEcommerceAction<Cart | CheckoutStep | EventSpecification>(modifiedCheckoutStep, trackers);
}
/**
 * Tracks a Share event specification.
 * ID: 59adf141-7c9a-422e-8ef2-639f7dbaf977
 */
export function trackShareSpec(share: Share & ContextsOrTimestamp<BlogPost | NewsArticle>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '59adf141-7c9a-422e-8ef2-639f7dbaf977',
        name: 'Share',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(share.context)
        ? [...share.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShare = {
        ...share,
        context,
    };

    trackShare<BlogPost | NewsArticle | EventSpecification>(modifiedShare, trackers);
}
/**
 * Tracks a CustomerMatchmakerStepComplete event specification.
 * ID: 6ff8b405-c540-4310-a033-b1b8ed99bb98
 */
export function trackCustomerMatchmakerStepCompleteSpec(customerMatchmakerStepComplete: WizardStepInteraction & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '6ff8b405-c540-4310-a033-b1b8ed99bb98',
        name: 'Customer Matchmaker Step Complete',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(customerMatchmakerStepComplete.context)
        ? [...customerMatchmakerStepComplete.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCustomerMatchmakerStepComplete = {
        ...customerMatchmakerStepComplete,
        context,
    };

    trackWizardStepInteraction<Record<string, unknown> | EventSpecification>(modifiedCustomerMatchmakerStepComplete, trackers);
}
/**
 * Tracks a ProductListClick event specification.
 * ID: 745921d8-5d7a-4483-bd6b-2ebde8a5e6f7
 */
export function trackProductListClickSpec(productListClick: EcommerceAction & ContextsOrTimestamp<Product>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '745921d8-5d7a-4483-bd6b-2ebde8a5e6f7',
        name: 'Product list click',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(productListClick.context)
        ? [...productListClick.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedProductListClick = {
        ...productListClick,
        context,
    };

    trackEcommerceAction<Product | EventSpecification>(modifiedProductListClick, trackers);
}
/**
 * Tracks a ChabotTrigger event specification.
 * ID: 7ea0e887-8efe-4a22-8418-8b300f2ee048
 */
export function trackChabotTriggerSpec(chabotTrigger: ChatbotInteraction & ContextsOrTimestamp<Chatbot>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '7ea0e887-8efe-4a22-8418-8b300f2ee048',
        name: 'Chabot Trigger',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(chabotTrigger.context)
        ? [...chabotTrigger.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChabotTrigger = {
        ...chabotTrigger,
        context,
    };

    trackChatbotInteraction<Chatbot | EventSpecification>(modifiedChabotTrigger, trackers);
}
/**
 * Tracks a FormError event specification.
 * ID: 9330b36c-9141-4067-bc55-3676bd040b82
 */
export function trackFormErrorSpec(formError: FormError & ContextsOrTimestamp<Form | UrPlusShowroomProduct>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: '9330b36c-9141-4067-bc55-3676bd040b82',
        name: 'Form Error',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(formError.context)
        ? [...formError.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormError = {
        ...formError,
        context,
    };

    trackFormError<Form | UrPlusShowroomProduct | EventSpecification>(modifiedFormError, trackers);
}
/**
 * Tracks a TabSelect event specification.
 * ID: a3b05169-e021-4716-9188-d7c37b7a3031
 */
export function trackTabSelectSpec(tabSelect: TabSelect & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'a3b05169-e021-4716-9188-d7c37b7a3031',
        name: 'Tab Select',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(tabSelect.context)
        ? [...tabSelect.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTabSelect = {
        ...tabSelect,
        context,
    };

    trackTabSelect<Record<string, unknown> | EventSpecification>(modifiedTabSelect, trackers);
}
/**
 * Tracks a ConsentPreferences event specification.
 * ID: badd80ff-804f-47be-ae31-96be97e163ac
 */
export function trackConsentPreferencesSpec(consentPreferences: ConsentPreferences & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'badd80ff-804f-47be-ae31-96be97e163ac',
        name: 'Consent Preferences',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(consentPreferences.context)
        ? [...consentPreferences.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedConsentPreferences = {
        ...consentPreferences,
        context,
    };

    trackConsentPreferences<Record<string, unknown> | EventSpecification>(modifiedConsentPreferences, trackers);
}
/**
 * Tracks a CustomerMatchmakerStepStart event specification.
 * ID: bedcd05a-5fae-448d-99aa-6c6b64e174f8
 */
export function trackCustomerMatchmakerStepStartSpec(customerMatchmakerStepStart: WizardStepInteraction & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'bedcd05a-5fae-448d-99aa-6c6b64e174f8',
        name: 'Customer Matchmaker Step Start',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(customerMatchmakerStepStart.context)
        ? [...customerMatchmakerStepStart.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCustomerMatchmakerStepStart = {
        ...customerMatchmakerStepStart,
        context,
    };

    trackWizardStepInteraction<Record<string, unknown> | EventSpecification>(modifiedCustomerMatchmakerStepStart, trackers);
}
/**
 * Tracks a ChabotUserTrigger event specification.
 * ID: bfcce0c4-a5b9-4a6c-8262-22650ca72652
 */
export function trackChabotUserTriggerSpec(chabotUserTrigger: ChatbotInteraction & ContextsOrTimestamp<Chatbot>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'bfcce0c4-a5b9-4a6c-8262-22650ca72652',
        name: 'Chabot User Trigger',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(chabotUserTrigger.context)
        ? [...chabotUserTrigger.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChabotUserTrigger = {
        ...chabotUserTrigger,
        context,
    };

    trackChatbotInteraction<Chatbot | EventSpecification>(modifiedChabotUserTrigger, trackers);
}
/**
 * Tracks a Login event specification.
 * ID: c6fe5c70-6a23-4f57-96b3-cde16727c432
 */
export function trackLoginSpec(login: UserDetect & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'c6fe5c70-6a23-4f57-96b3-cde16727c432',
        name: 'Login',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(login.context)
        ? [...login.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLogin = {
        ...login,
        context,
    };

    trackUserDetect<Record<string, unknown> | EventSpecification>(modifiedLogin, trackers);
}
/**
 * Tracks a ChabotFormComplete event specification.
 * ID: ced3f3aa-62f5-4179-ae37-c126c7163e34
 */
export function trackChabotFormCompleteSpec(chabotFormComplete: ChatbotInteraction & ContextsOrTimestamp<Chatbot>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'ced3f3aa-62f5-4179-ae37-c126c7163e34',
        name: 'Chabot Form Complete',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(chabotFormComplete.context)
        ? [...chabotFormComplete.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChabotFormComplete = {
        ...chabotFormComplete,
        context,
    };

    trackChatbotInteraction<Chatbot | EventSpecification>(modifiedChabotFormComplete, trackers);
}
/**
 * Tracks a ChabotButtonClick event specification.
 * ID: d6c4fc6f-b3a7-4bac-aa03-7b4a0403cd92
 */
export function trackChabotButtonClickSpec(chabotButtonClick: ChatbotInteraction & ContextsOrTimestamp<Chatbot>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'd6c4fc6f-b3a7-4bac-aa03-7b4a0403cd92',
        name: 'Chabot Button Click',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(chabotButtonClick.context)
        ? [...chabotButtonClick.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChabotButtonClick = {
        ...chabotButtonClick,
        context,
    };

    trackChatbotInteraction<Chatbot | EventSpecification>(modifiedChabotButtonClick, trackers);
}
/**
 * Tracks a ChabotStartDiscussion event specification.
 * ID: dab3ce78-e44a-489d-b7c8-6a15f2627a4e
 */
export function trackChabotStartDiscussionSpec(chabotStartDiscussion: ChatbotInteraction & ContextsOrTimestamp<Chatbot>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'dab3ce78-e44a-489d-b7c8-6a15f2627a4e',
        name: 'Chabot Start Discussion',
        data_product_id: '31d0139f-d00b-45a6-bdca-80be9067af61',
        data_product_name: 'Storyblok Corporate Website'
    });

    const context = Array.isArray(chabotStartDiscussion.context)
        ? [...chabotStartDiscussion.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChabotStartDiscussion = {
        ...chabotStartDiscussion,
        context,
    };

    trackChatbotInteraction<Chatbot | EventSpecification>(modifiedChabotStartDiscussion, trackers);
}
/**
 * Tracks a ProductView event specification.
 * ID: dfc9677f-7dcd-437c-b0be-a57786355893
 */
export function trackProductViewSpec(productView: EcommerceAction & ContextsOrTimestamp<Product>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'dfc9677f-7dcd-437c-b0be-a57786355893',
        name: 'Product view',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(productView.context)
        ? [...productView.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedProductView = {
        ...productView,
        context,
    };

    trackEcommerceAction<Product | EventSpecification>(modifiedProductView, trackers);
}
/**
 * Tracks a FormComplete event specification.
 * ID: e6b29bfc-ec11-4d9a-83d4-6a51ffe0fd2b
 */
export function trackFormCompleteSpec(formComplete: FormComplete & ContextsOrTimestamp<Form | UrPlusShowroomProduct>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'e6b29bfc-ec11-4d9a-83d4-6a51ffe0fd2b',
        name: 'Form Complete',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(formComplete.context)
        ? [...formComplete.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormComplete = {
        ...formComplete,
        context,
    };

    trackFormComplete<Form | UrPlusShowroomProduct | EventSpecification>(modifiedFormComplete, trackers);
}
/**
 * Tracks a Logout event specification.
 * ID: e7689c33-ab4a-4ba4-97ae-5a476fecc578
 */
export function trackLogoutSpec(logout: UserDetect & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'e7689c33-ab4a-4ba4-97ae-5a476fecc578',
        name: 'Logout',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(logout.context)
        ? [...logout.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLogout = {
        ...logout,
        context,
    };

    trackUserDetect<Record<string, unknown> | EventSpecification>(modifiedLogout, trackers);
}
/**
 * Tracks a ButtonClick event specification.
 * ID: e9d69e0f-94e5-444d-b7c5-2bdacda75a43
 */
export function trackButtonClickSpec(buttonClick: ButtonClick & ContextsOrTimestamp<Form | Page | User>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'e9d69e0f-94e5-444d-b7c5-2bdacda75a43',
        name: 'Button Click',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(buttonClick.context)
        ? [...buttonClick.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedButtonClick = {
        ...buttonClick,
        context,
    };

    trackButtonClick<Form | Page | User | EventSpecification>(modifiedButtonClick, trackers);
}
/**
 * Tracks a ProductListView event specification.
 * ID: ebd72e60-91ff-4be9-bf3b-c366809e9542
 */
export function trackProductListViewSpec(productListView: EcommerceAction & ContextsOrTimestamp<Product>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'ebd72e60-91ff-4be9-bf3b-c366809e9542',
        name: 'Product list view',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(productListView.context)
        ? [...productListView.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedProductListView = {
        ...productListView,
        context,
    };

    trackEcommerceAction<Product | EventSpecification>(modifiedProductListView, trackers);
}
/**
 * Tracks a TransactionError event specification.
 * ID: f0cb2423-177d-4124-9f75-6ca1bf6948d1
 */
export function trackTransactionErrorSpec(transactionError: EcommerceAction & ContextsOrTimestamp<Cart | Order | TransactionError>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'f0cb2423-177d-4124-9f75-6ca1bf6948d1',
        name: 'Transaction Error',
        data_product_id: '40cf2626-874d-45a7-81cb-14c1dbdf7e71',
        data_product_name: 'Marketplace'
    });

    const context = Array.isArray(transactionError.context)
        ? [...transactionError.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTransactionError = {
        ...transactionError,
        context,
    };

    trackEcommerceAction<Cart | Order | TransactionError | EventSpecification>(modifiedTransactionError, trackers);
}
/**
 * Tracks a FileDownload event specification.
 * ID: f2ba988b-79ed-4dbe-8d52-e3d6c4ec7a4e
 */
export function trackFileDownloadSpec(fileDownload: FileDownload & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'f2ba988b-79ed-4dbe-8d52-e3d6c4ec7a4e',
        name: 'File Download',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(fileDownload.context)
        ? [...fileDownload.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFileDownload = {
        ...fileDownload,
        context,
    };

    trackFileDownload<Record<string, unknown> | EventSpecification>(modifiedFileDownload, trackers);
}
/**
 * Tracks a FormStart event specification.
 * ID: f2f3d19c-f013-495c-8fb1-9be70544f5d7
 */
export function trackFormStartSpec(formStart: FormStart & ContextsOrTimestamp<Form | UrPlusShowroomProduct>, trackers?: string[]){
    const eventSpecificationContext: SelfDescribingJson<EventSpecification> = createEventSpecification({ 
        id: 'f2f3d19c-f013-495c-8fb1-9be70544f5d7',
        name: 'Form Start',
        data_product_id: 'a2087353-7a94-4ab6-a889-f5bd5e37ab86',
        data_product_name: 'Sirius Component Library'
    });

    const context = Array.isArray(formStart.context)
        ? [...formStart.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormStart = {
        ...formStart,
        context,
    };

    trackFormStart<Form | UrPlusShowroomProduct | EventSpecification>(modifiedFormStart, trackers);
}

